import React, { createContext, useContext, useEffect, useState } from "react";
 
export type UserRole = "PROVIDER" | "patient" | "admin";
 
export interface User {
  id: string | number;
  name: string;
  username?: string;
  email?: string;
  avatar?: string;
  role: UserRole;
  clinic?: string;
  token?: string;
}
 
interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  loginWithCredentials: (
    email: string,
    password: string,
    role: UserRole
  ) => Promise<User>;
  logout: () => void;
}
 
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/* ====================== HELPER FUNCTIONS FOR ROLE-SPECIFIC STORAGE ====================== */
const getStorageKey = (role: UserRole, suffix: string): string => {
  return `vivo_${role}_${suffix}`;
};
 
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
 
  // Load from localStorage on first render with role awareness
  const [user, setUser] = useState<User | null>(() => {
    try {
      console.log("=== AuthContext Initialization ===");
      console.log("Available localStorage keys:", Object.keys(localStorage));
      
      // Try to load patient user
      const patientRaw = localStorage.getItem(getStorageKey("patient", "user"));
      console.log("Patient data from storage:", patientRaw ? "Found" : "Not found");
      if (patientRaw) {
        const parsed = JSON.parse(patientRaw);
        if (parsed.role === "patient") {
          console.log("✓ Restored patient user from localStorage");
          return parsed;
        }
      }
      
      // Try to load provider user
      const providerRaw = localStorage.getItem(getStorageKey("PROVIDER", "user"));
      console.log("Provider data from storage:", providerRaw ? "Found" : "Not found");
      if (providerRaw) {
        const parsed = JSON.parse(providerRaw);
        if (parsed.role === "PROVIDER") {
          console.log("✓ Restored provider user from localStorage");
          return parsed;
        }
      }
      
      console.log("No user found in localStorage");
      return null;
    } catch (err) {
      console.error("Error loading user from localStorage:", err);
      return null;
    }
  });

  // Track if we're still loading initial user from localStorage
  const [isLoading, setIsLoading] = useState(true);

  // Initialize loading state - wait a tick to ensure everything is mounted
  useEffect(() => {
    // Use setTimeout to ensure this runs after render
    console.log("Setting isLoading to false, current user:", user);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 0);
    return () => clearTimeout(timer);
  }, [user]);
 
  // Sync user data to role-specific localStorage
  useEffect(() => {
    if (user) {
      console.log("Syncing user to localStorage:", user.role);
      const key = getStorageKey(user.role, "user");
      localStorage.setItem(key, JSON.stringify(user));
      
      // Also store important values with role-specific keys
      localStorage.setItem(getStorageKey(user.role, "token"), user.token || "");
      localStorage.setItem(getStorageKey(user.role, "id"), String(user.id) || "");
      localStorage.setItem(getStorageKey(user.role, "username"), user.username || "");
    } else {
      console.log("User is null, not syncing to localStorage");
    }
  }, [user]);
 
  /* ====================== LOGIN FUNCTION ====================== */
  const loginWithCredentials = async (
    email: string,
    password: string,
    role: UserRole
  ): Promise<User> => {
    try {
      const response = await fetch(
        "https://vivotal-backend.vercel.app/api/v1/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            password,
            role_type: role.toUpperCase(),
          }),
        }
      );
 
      const data = await response.json();
 
      if (!response.ok) {
        throw new Error(data?.message || "Invalid credentials");
      }
 
      const u = data?.data;
 
      // Construct user object
      const loggedUser: User = {
        id: u?.userId || u?.id,
        name: `${u?.first_name || ""} ${u?.last_name || ""}`.trim(),
        username: u?.username || u?.email?.split("@")[0] || "",
        email: u?.email,
        avatar: u?.avatar,
        role,
        clinic: u?.clinic,
        token: u?.token,
      };
 
      // Store full user object with role-specific key
      setUser(loggedUser);
 
      return loggedUser;
    } catch (err) {
      console.error("Login error:", err);
      throw err;
    }
  };
 
  /* ====================== LOGOUT FUNCTION ====================== */
  const logout = () => {
    if (user) {
      // Clear only the current user's role-specific data
      const role = user.role;
      localStorage.removeItem(getStorageKey(role, "user"));
      localStorage.removeItem(getStorageKey(role, "token"));
      localStorage.removeItem(getStorageKey(role, "id"));
      localStorage.removeItem(getStorageKey(role, "username"));
    }
    setUser(null);
  };
 
  return (
    <AuthContext.Provider value={{ user, isLoading, loginWithCredentials, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
 
/* ====================== USE HOOK ====================== */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

/* ====================== HELPER HOOK TO GET ROLE-SPECIFIC TOKEN ====================== */
export const useRoleSpecificToken = (role: UserRole): string | null => {
  const token = localStorage.getItem(getStorageKey(role, "token"));
  return token || null;
};

/* ====================== HELPER HOOK TO GET ROLE-SPECIFIC ID ====================== */
export const useRoleSpecificId = (role: UserRole): string | null => {
  const id = localStorage.getItem(getStorageKey(role, "id"));
  return id || null;
};
 
export default AuthContext;